import {
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Text,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatTime } from "@src/appV2/redesign/utils/formatTime";
import { differenceInMinutes, parseISO } from "date-fns";

import { LoadingButton } from "../../../components/LoadingButton";
import { formatMinutesDuration } from "../../../utils/formatMinutesDuration";
import { TimesheetField } from "../../components/TimesheetField";
import { TimesheetRow } from "../../components/TimesheetRow";
import { type MissedPunchRequestStatus } from "../types";

const EMPTY_VALUE = "--";

export interface TimesheetTimestamps {
  clockIn?: string;
  clockOut?: string;
  breakStart?: string;
  breakEnd?: string;
}

interface TimesheetListItemProps {
  verifiedTimesheet: TimesheetTimestamps;
  proposedTimesheet?: TimesheetTimestamps;
  isVerified: boolean;
  verifiedTimeInHours?: number;
  isFullShiftPayment: boolean;
  pendingApprovalInMinutes?: number;
  missedPunchRequestStatus: MissedPunchRequestStatus;
  isMissedPunchRequestEnabled: boolean;
  canSubmitTimesheet: boolean;
  facilityTimeZone: string;
  onRequestPayAdjustment?: () => void;
  isCheckingGeofence?: boolean;
}

function formatTimeValue(isoString: string | undefined, timezone: string): string {
  if (!isDefined(isoString)) {
    return EMPTY_VALUE;
  }

  return formatTime(parseISO(isoString), timezone);
}

function formatBreakDisplay(breakMinutes: number): string {
  return breakMinutes > 0 ? formatMinutesDuration(breakMinutes) : "Skipped";
}

function getVerifiedHoursDisplay(verifiedTimeInHours: number | undefined): string {
  if (!isDefined(verifiedTimeInHours)) {
    return EMPTY_VALUE;
  }

  return formatMinutesDuration(Math.round(verifiedTimeInHours * 60));
}

function getBreakDurationInMinutes(params: { breakStart?: string; breakEnd?: string }): number {
  const { breakStart, breakEnd } = params;

  if (isDefined(breakStart) && isDefined(breakEnd)) {
    return differenceInMinutes(parseISO(breakEnd), parseISO(breakStart));
  }

  return 0;
}

export function TimesheetListItem(props: TimesheetListItemProps) {
  const {
    verifiedTimesheet,
    proposedTimesheet,
    isVerified,
    verifiedTimeInHours,
    isFullShiftPayment,
    pendingApprovalInMinutes: pendingApprovalInMinutesProperty,
    missedPunchRequestStatus,
    isMissedPunchRequestEnabled,
    canSubmitTimesheet,
    facilityTimeZone,
    onRequestPayAdjustment,
    isCheckingGeofence,
  } = props;

  const isPending = missedPunchRequestStatus === "pending";

  const showRequestedClockIn =
    isPending &&
    isDefined(proposedTimesheet?.clockIn) &&
    proposedTimesheet.clockIn !== verifiedTimesheet.clockIn;
  const showRequestedClockOut =
    isPending &&
    isDefined(proposedTimesheet?.clockOut) &&
    proposedTimesheet.clockOut !== verifiedTimesheet.clockOut;

  const proposedBreakMinutes = getBreakDurationInMinutes({
    breakStart: proposedTimesheet?.breakStart,
    breakEnd: proposedTimesheet?.breakEnd,
  });
  const verifiedBreakMinutes = getBreakDurationInMinutes({
    breakStart: verifiedTimesheet.breakStart,
    breakEnd: verifiedTimesheet.breakEnd,
  });
  const showRequestedBreak = isPending && proposedBreakMinutes !== verifiedBreakMinutes;

  const hasVerifiedClockTimes =
    isDefined(verifiedTimesheet.clockIn) && isDefined(verifiedTimesheet.clockOut);
  const showBreak = hasVerifiedClockTimes || isPending;

  const showRequestPayAdjustmentButton =
    isVerified &&
    missedPunchRequestStatus === "none" &&
    isMissedPunchRequestEnabled &&
    canSubmitTimesheet;

  const pendingApprovalInMinutes = isPending ? pendingApprovalInMinutesProperty : undefined;

  const verifiedHoursValue = isVerified
    ? getVerifiedHoursDisplay(verifiedTimeInHours)
    : EMPTY_VALUE;

  const pendingApprovalTime = isDefined(pendingApprovalInMinutes)
    ? formatMinutesDuration(pendingApprovalInMinutes)
    : undefined;

  return (
    <ListItem divider>
      <ListItemIcon iconType="clock-filled" />
      <ListItemText
        disableTypography
        primary={
          <Text semibold variant="h6">
            Timesheet
          </Text>
        }
        secondary={
          <Stack spacing={4} sx={{ marginTop: 4 }}>
            <Stack spacing={4}>
              <TimesheetField
                label="Clock in"
                showRequested={showRequestedClockIn}
                verifiedValue={formatTimeValue(verifiedTimesheet.clockIn, facilityTimeZone)}
                proposedValue={formatTimeValue(proposedTimesheet?.clockIn, facilityTimeZone)}
                hasError={!isDefined(verifiedTimesheet.clockIn)}
              />

              {showBreak && (
                <TimesheetField
                  label="Break"
                  showRequested={showRequestedBreak}
                  verifiedValue={
                    hasVerifiedClockTimes ? formatBreakDisplay(verifiedBreakMinutes) : EMPTY_VALUE
                  }
                  proposedValue={formatBreakDisplay(proposedBreakMinutes)}
                />
              )}

              <TimesheetField
                label="Clock out"
                showRequested={showRequestedClockOut}
                verifiedValue={formatTimeValue(verifiedTimesheet.clockOut, facilityTimeZone)}
                proposedValue={formatTimeValue(proposedTimesheet?.clockOut, facilityTimeZone)}
                hasError={!isDefined(verifiedTimesheet.clockOut)}
              />
            </Stack>

            <Divider />

            <Stack spacing={4}>
              <TimesheetRow bold label="Verified hours" value={verifiedHoursValue} />
              {isDefined(pendingApprovalTime) && (
                <TimesheetRow
                  bold
                  highlight
                  label="Pending approval"
                  value={isFullShiftPayment ? pendingApprovalTime : `+${pendingApprovalTime}`}
                />
              )}
            </Stack>

            {showRequestPayAdjustmentButton && (
              <LoadingButton
                fullWidth
                variant="outlined"
                size="large"
                isLoading={isCheckingGeofence ?? false}
                onClick={onRequestPayAdjustment}
              >
                Request pay adjustment
              </LoadingButton>
            )}
          </Stack>
        }
      />
    </ListItem>
  );
}
